import { Box, Flex } from '@chakra-ui/layout';
import {
  type CollisionDetection,
  DndContext,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { type RefObject, useCallback, useEffect, useState } from 'react';

import { FeatureFlag } from '@revelio/core';

import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import { useGlobalAIAssistantFeatureEnabled } from '../../../global-ai-assistant/use-global-ai-assistant-feature-enabled';
import { useGridLayout } from '../../hooks/useGridLayout';
import { AIAssistantDrawer } from '../AIAssistant/AIAssistantDrawer';
import { AIAssistantFAB } from '../AIAssistant/AIAssistantFAB';
import { Chart } from '../Chart/ChartRoot/Chart';
import { ChartBuilderPanel } from '../ChartConfig/ChartBuilderPanel';
import { useEditMode } from '../EditModeProvider/EditModeProvider';
import { DEFAULT_GRID_ROWS } from '../PageConfigProvider/constants';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import { GhostPlaceholders } from './components/GhostPlaceholders';
import {
  DASHBOARD_ACTION_BAR_HEIGHT,
  RESIZE_HANDLE_PADDING,
} from './constants';
import { Draggable } from './Draggable';
import styles from './EditableGrid.module.css';
import { EditGrid } from './EditGrid';
import { GridActionButton } from './GridActionButton';
import { useGrid } from './GridProvider';
import { getEntityTypeFromPrimaryFilter } from './getEntityTypeFromPrimaryFilter';
import { useEditableGrid } from './hooks/useEditableGrid';
import { calculateGhosts } from './utils/ghost-layout-generator';

const customCollisionDetectionAlgorithm: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length > 0) {
    return pointerCollisions;
  }
  return rectIntersection(args);
};

interface DashboardEditableGridProps {
  onBuilderOpenChange?: (isOpen: boolean) => void;
  onAIDrawerOpenChange?: (isOpen: boolean) => void;
  gridContainerRef: RefObject<HTMLDivElement>;
}

export const DashboardEditableGrid = ({
  onBuilderOpenChange,
  onAIDrawerOpenChange,
  gridContainerRef,
}: DashboardEditableGridProps) => {
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 10 },
  });
  const sensors = useSensors(pointerSensor);

  const { gridItems, grid, edgeDragging, itemDragging } = useGrid();
  const { primaryFilter, dispatch } = useTabContentConfig();
  const gridLayout = useGridLayout({
    isReport: false,
    gridRows: grid.rows,
    gridColumns: grid.columns,
  });

  const {
    openConfigId,
    closeConfig,
    selectedIds,
    clearSelection,
    handleChartClick,
    filteredGridItems,
    selectedItem,
    handleAddItem,
    handleDragStart,
    handleDragEnd,
  } = useEditableGrid({ onBuilderOpenChange });

  const [dragTransform, setDragTransform] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const onDragEnd = useCallback(
    (event: Parameters<typeof handleDragEnd>[0]) => {
      handleDragEnd(event);
      setDragTransform(null);
    },
    [handleDragEnd]
  );

  const [isAIDrawerOpen, setIsAIDrawerOpen] = useState(false);
  const { editMode } = useEditMode();
  const isAIDashGenEnabled = useUnleashFlag(FeatureFlag.AIPageBuilder);
  const isGlobalAiAssistantEnabled = useGlobalAIAssistantFeatureEnabled();

  const entityType = getEntityTypeFromPrimaryFilter(primaryFilter);
  const canUseAI = entityType !== null && editMode;

  const handleAIDrawerOpen = () => {
    if (openConfigId) {
      closeConfig();
    }
    setIsAIDrawerOpen(true);
    onAIDrawerOpenChange?.(true);
  };

  const handleAIDrawerClose = () => {
    setIsAIDrawerOpen(false);
    onAIDrawerOpenChange?.(false);
  };

  // Close AI drawer when builder opens
  useEffect(() => {
    if (openConfigId && isAIDrawerOpen) {
      setIsAIDrawerOpen(false);
      onAIDrawerOpenChange?.(false);
    }
  }, [openConfigId, isAIDrawerOpen, onAIDrawerOpenChange]);

  const ghostPlaceholders = calculateGhosts({
    items: gridItems,
    grid: { rows: grid.rows, columns: grid.columns },
    isReport: false,
  });

  const addRowSize = DEFAULT_GRID_ROWS / 2;
  const handleAddRows = () => {
    dispatch({ type: 'ADD_ROWS', payload: { rows: addRowSize } });
  };

  const maxRenderedRow = filteredGridItems.reduce(
    (max, item) => Math.max(max, item.position.y + item.position.height),
    0
  );
  const showRemoveRowsButton =
    grid?.rows && grid?.rows > Math.max(maxRenderedRow, DEFAULT_GRID_ROWS);
  const handleRemoveRows = () => {
    const excessRows = grid?.rows - Math.max(maxRenderedRow, DEFAULT_GRID_ROWS);
    if (excessRows <= 0) return;
    if (excessRows < addRowSize) {
      dispatch({ type: 'REMOVE_ROWS', payload: { rows: excessRows } });
    } else {
      dispatch({ type: 'REMOVE_ROWS', payload: { rows: addRowSize } });
    }
  };

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={onDragEnd}
      collisionDetection={customCollisionDetectionAlgorithm}
      modifiers={[restrictToParentElement]}
      sensors={sensors}
      autoScroll={false}
    >
      {(itemDragging || edgeDragging) && (
        <EditGrid
          columns={grid.columns}
          rows={grid.rows}
          width={grid.width}
          height={grid.height}
          paddingLeft={grid.paddingLeft}
          paddingTop={grid.paddingTop}
          trackWidth={grid.trackWidth}
          trackHeight={grid.trackHeight}
          columnGap={grid.columnGap}
          rowGap={grid.rowGap}
        />
      )}
      <Box
        id="editable-grid-container"
        ref={gridContainerRef}
        display="grid"
        position="relative"
        zIndex={1}
        gap="1rem"
        width="100%"
        height={`calc(100% - ${DASHBOARD_ACTION_BAR_HEIGHT})`}
        minWidth="0"
        minHeight="0"
        gridTemplateRows={gridLayout.styles.gridTemplateRows}
        gridTemplateColumns={gridLayout.styles.gridTemplateColumns}
        padding={RESIZE_HANDLE_PADDING}
        className={styles.grid}
        overflow="hidden"
      >
        <GhostPlaceholders
          ghosts={ghostPlaceholders}
          onAddItem={handleAddItem}
        />
        {filteredGridItems.map((item) => (
          <Draggable
            id={item.id}
            key={item.id}
            item={item}
            focused={item.id === openConfigId}
            selected={selectedIds.has(item.id)}
            dragTransform={dragTransform}
            setDragTransform={setDragTransform}
            selectedIds={selectedIds}
            style={{
              gridColumn: `${item.position.x + 1} / span ${item.position.width}`,
              gridRow: `${item.position.y + 1} / span ${item.position.height}`,
            }}
          >
            <Chart
              item={item}
              headerSize={
                item.position.height / grid.rows <= 0.25 ? 'sm' : 'md'
              }
              primaryFilter={primaryFilter}
              onClick={(e) => handleChartClick(item.id, e)}
              {...((!!itemDragging || !!edgeDragging) && {
                pointerEvents: 'none',
              })}
              variant="default"
            />
          </Draggable>
        ))}
        <ChartBuilderPanel
          isOpen={!!openConfigId}
          item={selectedItem}
          selectedIds={selectedIds}
          clearSelection={clearSelection}
          onClose={closeConfig}
        />
      </Box>
      <Flex
        backgroundColor="transparent"
        position="absolute"
        bottom="0"
        width="100%"
        height={DASHBOARD_ACTION_BAR_HEIGHT}
        opacity="1"
        gap="1rem"
        zIndex={100}
        justifyContent="center"
        alignItems="center"
      >
        <GridActionButton
          label="+ Add Rows"
          onClick={handleAddRows}
          additionalActions={
            showRemoveRowsButton
              ? [
                  {
                    label: '- Remove Rows',
                    onClick: handleRemoveRows,
                    isDisabled: !showRemoveRowsButton,
                  },
                ]
              : undefined
          }
          menuButtonProps={{
            'aria-label': 'More options',
          }}
        />
      </Flex>
      {/* AI Assistant FAB and Drawer */}
      {isAIDashGenEnabled &&
        canUseAI &&
        entityType &&
        !isGlobalAiAssistantEnabled && (
          <>
            <AIAssistantFAB
              onClick={handleAIDrawerOpen}
              isVisible={!isAIDrawerOpen}
            />
            <AIAssistantDrawer
              isOpen={isAIDrawerOpen}
              onClose={handleAIDrawerClose}
              mode="dashboard"
              entityType={entityType}
            />
          </>
        )}
    </DndContext>
  );
};
